import {
  AnimationAction,
  AnimationMixer,
  LoopOnce,
  LoopRepeat,
  type AnimationActionLoopStyles,
} from 'three';
import { Quaternion, Vector3, type Object3D } from 'three';
import type { CharacterInstance } from './CharacterModel';

export type LocomotionState =
  | 'idle'
  | 'walk'
  | 'sprint'
  | 'jump_start'
  | 'jump_loop'
  | 'jump_land'
  | 'dance'
  | 'swim_idle'
  | 'swim_fwd'
  | 'fish_idle'
  | 'fish_cast'
  | 'fish_bite'
  | 'fish_reel'
  | 'fish_catch'
  | 'attack'
  | 'dead';

/** Mapa stavu na klip pro vzdálené hráče. */
const REMOTE_CLIPS: Record<LocomotionState, string> = {
  idle: 'Idle_Loop',
  walk: 'Walk_Loop',
  sprint: 'Sprint_Loop',
  jump_start: 'Jump_Start',
  jump_loop: 'Jump_Loop',
  jump_land: 'Jump_Land',
  dance: 'Dance_Loop',
  swim_idle: 'Swim_Idle_Loop',
  swim_fwd: 'Swim_Fwd_Loop',
  fish_idle: 'Fishing_Idle',
  fish_cast: 'Fishing_Cast',
  fish_bite: 'Fishing_Bite',
  fish_reel: 'Fishing_Reeling',
  fish_catch: 'Fishing_Catch',
  attack: 'Sword_Attack',
  dead: 'Death01',
};

/** Stavy, ve kterých postava svírá prut. */
const FISHING_STATES = new Set<LocomotionState>([
  'fish_idle', 'fish_cast', 'fish_bite', 'fish_reel', 'fish_catch',
]);

/**
 * Sevření prstů kolem prutu.
 *
 * KayKit rig, ze kterého rybářské animace pocházejí, prsty vůbec nemá —
 * po přenosu proto zůstávají v klidové pozici, což je u Quaternius rigu
 * natažená dlaň. Postava pak drží prut roztaženými prsty.
 *
 * Pozice se dopisuje až za mixérem, ne do klipu: kdyby se pekla v Blenderu,
 * záviselo by to na tom, jak se při převodu otočí lokální osy kostí. Takhle
 * se rotace zapisují ve stejném souřadném systému, v jakém je čte three.js.
 *
 * Levá a pravá ruka mají zrcadlené kosti, proto opačná znaménka.
 */
const GRIP_FINGER = 0.9;
const GRIP_THUMB = 0.5;

/** Pootočení kolem lokální osy Z, tedy směr, kterým se prst ohýbá. */
function curlQuaternion(angle: number): Quaternion {
  return new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), angle);
}

export class CharacterAnimator {
  readonly mixer: AnimationMixer;
  private readonly actions = new Map<string, AnimationAction>();
  private currentState: LocomotionState = 'idle';
  private activeAction: AnimationAction | null = null;
  private isDancing = false;
  private isJumping = false;
  /**
   * Zastaralé. Sevření se dopisuje **jen** rybářským animacím.
   *
   * Quaternius klipy (chůze, běh, seknutí mečem) mají prsty animované
   * a přepisovat je znamená rozbít, co bylo správně. Ponecháno, aby
   * volající kód nemusel zmizet naráz — na nic se nenapojuje.
   */
  holdingItem = false;
  /**
   * Kosti prstů a jejich dodatečné pootočení.
   *
   * Ukládá se **kvaternion přírůstku**, ne cílová rotace. Sevření se pak
   * skládá s tím, co do kosti zapsala animace, místo aby to přepsalo —
   * což byla chyba, kvůli které měla postava překroucené dlaně: prstům
   * se tím zahazovala jejich klidová orientace z rigu.
   */
  private readonly gripBones: { bone: Object3D; curl: Quaternion }[] = [];

  constructor(character: CharacterInstance) {
    this.mixer = new AnimationMixer(character.root);

    for (const finger of ['index', 'middle', 'ring', 'pinky']) {
      for (const joint of ['01', '02', '03']) {
        const left = character.root.getObjectByName(`${finger}_${joint}_l`);
        const right = character.root.getObjectByName(`${finger}_${joint}_r`);
        if (left) this.gripBones.push({ bone: left, curl: curlQuaternion(GRIP_FINGER) });
        if (right) this.gripBones.push({ bone: right, curl: curlQuaternion(-GRIP_FINGER) });
      }
    }
    for (const joint of ['01', '02', '03']) {
      const left = character.root.getObjectByName(`thumb_${joint}_l`);
      const right = character.root.getObjectByName(`thumb_${joint}_r`);
      if (left) this.gripBones.push({ bone: left, curl: curlQuaternion(GRIP_THUMB) });
      if (right) this.gripBones.push({ bone: right, curl: curlQuaternion(-GRIP_THUMB) });
    }

    // Bind all clips into actions
    for (const [name, clip] of character.animations) {
      const action = this.mixer.clipAction(clip);
      this.actions.set(name, action);
    }

    // Start with Idle
    this.playAction('Idle_Loop', 0.1, LoopRepeat);
  }

  get state(): LocomotionState {
    return this.currentState;
  }

  get dancing(): boolean {
    return this.isDancing;
  }

  get jumping(): boolean {
    return this.isJumping;
  }

  private playAction(
    clipName: string,
    crossFadeDuration = 0.2,
    loopMode: AnimationActionLoopStyles = LoopRepeat,
  ): AnimationAction | null {
    const nextAction = this.actions.get(clipName);
    if (!nextAction) {
      console.warn(`Animation clip ${clipName} not found`);
      return null;
    }

    if (this.activeAction === nextAction) {
      if (loopMode === LoopOnce) {
        nextAction.reset();
        nextAction.play();
      }
      return nextAction;
    }

    nextAction.reset();
    nextAction.setLoop(loopMode, loopMode === LoopOnce ? 1 : Infinity);
    nextAction.clampWhenFinished = loopMode === LoopOnce;

    if (this.activeAction) {
      this.activeAction.fadeOut(crossFadeDuration);
      nextAction.fadeIn(crossFadeDuration);
    }

    nextAction.play();
    this.activeAction = nextAction;
    return nextAction;
  }

  /** Trigger dance emote. Moving WASD will cancel it automatically. */
  toggleDance(): void {
    if (this.isDancing) {
      this.stopDance();
    } else {
      this.isDancing = true;
      this.currentState = 'dance';
      this.playAction('Dance_Loop', 0.25, LoopRepeat);
    }
  }

  stopDance(): void {
    if (!this.isDancing) return;
    this.isDancing = false;
    this.currentState = 'idle';
    this.playAction('Idle_Loop', 0.2, LoopRepeat);
  }

  /** Trigger jump cycle */
  triggerJump(): void {
    if (this.isJumping) return;
    this.isDancing = false;
    this.isJumping = true;
    this.currentState = 'jump_start';

    const startAction = this.playAction('Jump_Start', 0.1, LoopOnce);
    if (startAction) {
      const onFinished = (e: { action: AnimationAction }) => {
        if (e.action === startAction && this.isJumping) {
          this.mixer.removeEventListener('finished', onFinished);
          this.currentState = 'jump_loop';
          this.playAction('Jump_Loop', 0.15, LoopRepeat);
        }
      };
      this.mixer.addEventListener('finished', onFinished);
    }
  }

  /**
   * Ukončí skok bez dopadové animace.
   *
   * Dopad do vody není dopad na zem — přehrát `Jump_Land` by znamenalo, že
   * postava ve vodě podřepne. Zrušit příznak ale musíme: `setMovementState`
   * kvůli běžícímu skoku končí hned na prvním řádku, takže dokud skok trvá,
   * plavecká animace se vůbec nespustí a postava zůstane viset v póze ze
   * skoku. Přesně tak vypadalo to "levitování" ve vodě.
   */
  cancelJump(): void {
    if (!this.isJumping) return;
    this.isJumping = false;
    // Stav musí být jiný než ten cílový, jinak si setMovementState myslí,
    // že už běží, a nepřepne.
    this.currentState = 'idle';
  }

  landJump(): void {
    if (!this.isJumping) return;
    this.isJumping = false;
    this.currentState = 'jump_land';

    const landAction = this.playAction('Jump_Land', 0.08, LoopOnce);
    if (landAction) {
      const onFinished = (e: { action: AnimationAction }) => {
        if (e.action === landAction && !this.isJumping) {
          this.mixer.removeEventListener('finished', onFinished);
          this.currentState = 'idle';
          this.playAction('Idle_Loop', 0.15, LoopRepeat);
        }
      };
      this.mixer.addEventListener('finished', onFinished);
    }
  }

  /** Update movement locomotion state (called from PlayerController) */
  setMovementState(isMoving: boolean, isSprinting: boolean, isSwimming = false): void {
    if (this.isJumping) return;

    if (isMoving && this.isDancing) {
      this.stopDance();
    }

    if (isSwimming) {
      const targetState: LocomotionState = isMoving ? 'swim_fwd' : 'swim_idle';
      if (this.currentState !== targetState) {
        this.currentState = targetState;
        this.playAction(isMoving ? 'Swim_Fwd_Loop' : 'Swim_Idle_Loop', 0.25);
      }
      return;
    }

    if (this.isDancing) return;

    let targetState: LocomotionState = 'idle';
    let clipName = 'Idle_Loop';

    if (isMoving) {
      if (isSprinting) {
        targetState = 'sprint';
        clipName = 'Sprint_Loop';
      } else {
        targetState = 'walk';
        clipName = 'Walk_Loop';
      }
    }

    if (this.currentState !== targetState) {
      this.currentState = targetState;
      this.playAction(clipName, 0.18);
    }
  }

  /**
   * Nastaví stav přímo, bez odvozování z pohybu.
   *
   * Vzdálení hráči posílají hotový stav lokomoce — přepočítávat ho z
   * interpolované pozice by znamenalo hádat, jestli chodí nebo běží,
   * a při každém zakolísání sítě by animace poskočila.
   */
  applyRemoteState(state: LocomotionState): void {
    if (this.currentState === state) return;
    this.currentState = state;

    const clip = REMOTE_CLIPS[state];
    const loop = state === 'jump_start' || state === 'jump_land' ? LoopOnce : LoopRepeat;
    this.playAction(clip, 0.18, loop);
  }

  /**
   * Spustí rybářský stav. Jednorázové klipy (nahození, zásek, vytažení)
   * se nesmí zacyklit, jinak by postava nahazovala donekonečna.
   */
  playFishing(state: LocomotionState): void {
    const clip = REMOTE_CLIPS[state];
    if (!clip) return;
    this.isDancing = false;
    this.currentState = state;
    const loop = state === 'fish_idle' || state === 'fish_reel' ? LoopRepeat : LoopOnce;
    this.playAction(clip, 0.2, loop);
  }

  /**
   * 3-stupňová kombinace útoků mečem:
   * Sek 1 -> Sword_Regular_A
   * Sek 2 -> Sword_Regular_B
   * Sek 3 -> Sword_Regular_C
   */
  playAttack(comboStep = 0): string {
    this.isDancing = false;
    this.currentState = 'attack';
    const clips = ['Sword_Regular_A', 'Sword_Regular_B', 'Sword_Regular_C'];
    const clipName = clips[comboStep % clips.length] ?? 'Sword_Regular_A';
    this.playAction(clipName, 0.05, LoopOnce);
    return clipName;
  }

  /** Smrt. Zůstává v poslední pozici, dokud se hráč nevrátí do hry. */
  playDeath(): void {
    this.isDancing = false;
    this.isJumping = false;
    this.currentState = 'dead';
    const action = this.playAction('Death01', 0.15, LoopOnce);
    if (action) action.clampWhenFinished = true;
  }

  update(dt: number): void {
    this.mixer.update(dt);

    // Musí se přepsat až za mixérem — ten do kostí zapisuje každý snímek
    // a cokoliv nastaveného před ním by rovnou přemazal.
    // Jen rybaření. Zdrojové animace z KayKit rigu prsty nemají, takže
    // jim sevření musíme dopsat. Všechno ostatní si prsty animuje samo
    // a sahat na ně by je jen pokazilo.
    if (FISHING_STATES.has(this.currentState)) {
      for (const { bone, curl } of this.gripBones) bone.quaternion.multiply(curl);
    }
  }

  dispose(): void {
    this.mixer.stopAllAction();
  }
}
